import React, { useState, Suspense, useEffect } from 'react';
import { ProjectAdminSubContainerStyled, FooterStyled, FooterButtonStyled } from '../../styled';
import { SpinnerSize, Box, Heading, Text, Spinner } from '@bechtel/gds';
import '@bechtel/gds/dist/index.css';
import { StatusContainerStyled, InputTimeStyled } from './styled';
import { ADD_IMPORT_SCHEDULE, GET_IMPORT_SCHEDULE } from '@bechtel/client-gql';
import { useMutation, useQuery } from '@apollo/client';
import {
  AddImportScheduleInput,
  AddImportSchedulePayload,
  ImportSchedule,
  ImportType,
  ImportStatusCode,
} from '@bechtel/types';
import { DateField, TextFieldSize, FormControl, Separator, SeparatorType, ButtonAppearance } from '@bechtel/gds';
import { get, orderBy, filter, first } from 'lodash';
import { differenceInMinutes, addDays } from 'date-fns';
import ImportItem from './result/item';
import { Formik, FormikProps, useField } from 'formik';
import { date, object, string } from 'yup';
import { ErrorRetry, SuspenseLoader, useDate, ErrorBoundary } from '@bechtel/client-common';
import { ImportScheduleErrorExt, ImportStatusCodeExt, InitialImportStatus } from './types';
import { ImportWpScheduleError } from './error';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ImportStatusCodeMessages } from '~/components/atoms/i18n-message-keys';

interface FormikState {
  scheduleDate: string;
  scheduleTime: string;
}
let formikInitialValues: FormikState = {
  scheduleDate: '',
  scheduleTime: '',
};
const ErrorField = ({ error }) => {
  const { i18n } = useLingui();
  return (
    <Box
      mt={{ sm: '300' }}
      css={`
        position: absolute;
        bottom: -${({ theme }) => theme.spacings[500]};
      `}
    >
      <Text size="400" weight="300" color="bechtel.red">
        {i18n._(error)}
      </Text>
    </Box>
  );
};
const dateTimeValidationSchema = object().shape({
  scheduleDate: date()
    .min(new Date(), t`Please select future date.`)
    .required(t`Please select start date.`),
  scheduleTime: string().required(t`Please select time.`),
});
const SelectDate = ({ name }) => {
  const { formatISODate } = useDate();
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  return (
    <Box pr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <FormControl style={{ flexGrow: 1 }} label={t`Start Date`}>
        <DateField
          {...field}
          selectsStart
          selected={field.value ? new Date(field.value) : null}
          minDate={addDays(new Date(), 1)}
          onChange={(selectedDate) => {
            setValue(formatISODate(selectedDate.toISOString()));
          }}
          showPopperArrow={false}
          placeholderText={t`Start Date`}
          size={TextFieldSize.LARGE}
          icon="calendar-alt"
          iconStyle="duo"
        />
      </FormControl>
      {meta.touched && meta.error ? <ErrorField error={meta.error} /> : null}
    </Box>
  );
};
const SelectTime = ({ name }) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  return (
    <Box pr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <FormControl style={{ flexGrow: 1 }} label={t`Start Time`}>
        <InputTimeStyled
          {...field}
          value={field.value ? field.value : ''}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
      </FormControl>
      {meta.touched && meta.error ? <ErrorField error={meta.error} /> : null}
    </Box>
  );
};

export const ImportWorkPackages = () => {
  const { formatISODate, formatTime } = useDate();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const [importScheduleError, setImportScheduleError] = useState<ImportScheduleErrorExt | null>();
  const {
    loading,
    error: wpScheduleError,
    data: scheduleData,
    refetch: wpScheduleRefetch,
  } = useQuery<{
    importSchedule: ImportSchedule[];
  }>(GET_IMPORT_SCHEDULE, { notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only' });

  const existingSchedules = get(scheduleData, 'importSchedules', []);
  //TODO:Need to apply this before order : and schedule.importStatus.status === ImportStatus.COMPLETED)
  const filteredData = filter(existingSchedules, (schedule) => schedule.importType === ImportType.WORK_PACKAGES);
  const workpackageSchedule =
    filteredData.length > 0 ? first(orderBy(filteredData, ['startDatetime'], ['desc'])) : null;
  const scheduledStartDate = workpackageSchedule ? formatISODate(workpackageSchedule.startDatetime) : '';
  const scheduledStartTime = workpackageSchedule ? formatTime(workpackageSchedule.startDatetime, 'HH:m') : '';

  const [scheduleStatus, setScheduleStatus] = useState<ImportStatusCodeExt>(InitialImportStatus.NOT_STARTED);
  const { i18n } = useLingui();
  useEffect(() => {
    if (workpackageSchedule) {
      setScheduleStatus(workpackageSchedule.importStatus.status);
      formikInitialValues = {
        ...formikInitialValues,
        scheduleDate: scheduledStartDate || '',
        scheduleTime: scheduledStartTime || '',
      };
      setInitialValues(formikInitialValues);
    }
  }, [workpackageSchedule, scheduledStartDate, scheduledStartTime]);

  const getScheduleDate = (values: FormikState): Date => {
    const newDate = `${values.scheduleDate} ${values.scheduleTime}`;
    return new Date(newDate);
  };
  const getFormattedDate = (date: string): string => {
    const time = formatTime(date) || '';
    const day = formatISODate(date) || '';
    return `${time.toLowerCase()} ${day}`;
  };
  const getScheduedCompletedTime = (scheduleStartDate: Date, scheduleEndDate: Date): string => {
    if (!scheduleStartDate || !scheduleEndDate) return '';
    const minutesInHrs = 60;
    const minutes = differenceInMinutes(new Date(scheduleEndDate), new Date(scheduleStartDate));
    const hours = minutes / minutesInHrs;
    const rhours = Math.floor(hours);
    const rminutes = (hours - rhours) * 60;
    const remainingMinutes = Math.round(rminutes);
    return (rhours > 0 ? `${rhours}h ` : '') + (remainingMinutes > 0 ? remainingMinutes + t`minutes` : '');
  };
  const [updateWorkpackageSchedule, { loading: mutationLoading }] = useMutation<
    { addImportSchedulePayload: AddImportSchedulePayload },
    { input: AddImportScheduleInput }
  >(ADD_IMPORT_SCHEDULE, {
    onCompleted: (data) => {
      const error = get(data, 'addImportSchedule.error');
      if (error) {
        setImportScheduleError(error);
      } else {
        setImportScheduleError(null);
        const importSchedule = get(data, 'addImportSchedule.importSchedule');
        const ImportStartDate = importSchedule ? formatISODate(importSchedule.startDatetime) : '';
        const ImportStartTime = importSchedule ? formatTime(importSchedule.startDatetime, 'HH:m') : '';
        formikInitialValues = {
          ...formikInitialValues,
          scheduleDate: ImportStartDate || '',
          scheduleTime: ImportStartTime || '',
        };
        setInitialValues(formikInitialValues);
        setScheduleStatus(importSchedule.importStatus.status);
      }
    },
    onError: (error) => {
      setImportScheduleError(error);
    },
  });
  const closeModal = () => {
    setImportScheduleError(null);
  };

  return (
    <ProjectAdminSubContainerStyled>
      {!loading && wpScheduleError && (
        <div data-testid="wp-discipline-error-retry">
          <ErrorRetry refetch={wpScheduleRefetch}>
            <Trans>
              There was a problem loading Initial Work Package Import.Please click the button below to try again.
            </Trans>
          </ErrorRetry>
        </div>
      )}
      {!loading && !wpScheduleError && (
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={dateTimeValidationSchema}
          onSubmit={async (values: FormikState) => {
            await updateWorkpackageSchedule({
              variables: {
                input: {
                  importType: ImportType.WORK_PACKAGES,
                  scheduleDatetime: getScheduleDate(values),
                },
              },
            });
          }}
        >
          {(formikProps: FormikProps<FormikState>) => {
            const { handleSubmit, handleReset, dirty, isValid } = formikProps;
            return (
              <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                <Box px={{ sm: '600' }} pt={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                  <Box alignItems={{ sm: 'center' }} pb={{ sm: '500' }}>
                    <Box pr={{ sm: '500' }}>
                      <Heading color="grey.1000" weight="600" size="500">
                        <Trans>Initial Work Package Import</Trans>
                      </Heading>
                    </Box>
                    <StatusContainerStyled status={scheduleStatus}>
                      <Text size="200" color="gray.600" transform="capitalize">
                        <Trans>Status:</Trans>
                        {i18n._(ImportStatusCodeMessages[scheduleStatus].id).toLowerCase().replace('_', ' ')}
                      </Text>
                    </StatusContainerStyled>
                    {scheduleStatus == ImportStatusCode.IN_PROGRESS && (
                      <Box ml={{ sm: '500' }}>
                        <Spinner />
                      </Box>
                    )}
                  </Box>
                  {scheduleStatus != ImportStatusCode.COMPLETED && (
                    <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                      <Box flexDirection={{ sm: 'column' }} pb={{ sm: '500' }}>
                        <Text size="300" family="graphik" color="grey.1000" style={{ lineHeight: '2' }}>
                          <Trans>Schedule a time to import all available work packages from the database.</Trans>
                        </Text>
                        <Text size="300" family="graphik" color="grey.1000">
                          <Trans>This is one-time action; any future updates will be automatically published.</Trans>
                        </Text>
                      </Box>
                      <Box pb={{ sm: '700' }}>
                        <Text size="300" family="graphik" weight="600" color="grey.600">
                          <Trans>
                            This may take several hours. Pick a time that doesn&apos;t interfere with your work.
                          </Trans>
                        </Text>
                      </Box>
                    </Box>
                  )}
                  <Box style={{ position: 'relative' }}>
                    <SelectDate name="scheduleDate"></SelectDate>
                    <SelectTime name="scheduleTime"></SelectTime>
                  </Box>
                  <Separator type={SeparatorType.HORIZONTAL} spacing="600" />
                  {workpackageSchedule && workpackageSchedule.importStatus.status == ImportStatusCode.COMPLETED && (
                    <Box flexDirection={{ sm: 'column' }} pb={{ sm: '500' }}>
                      <Box alignItems={{ sm: 'center' }} pb={{ sm: '600' }}>
                        <Box pr={{ sm: '500' }}>
                          <Heading color="grey.1000" weight="600" size="500" family="graphik">
                            <Trans>Import Results</Trans>
                          </Heading>
                        </Box>
                      </Box>
                      <ImportItem
                        label={t`Import Started:`}
                        value={getFormattedDate(workpackageSchedule.startDatetime)}
                      ></ImportItem>
                      <ImportItem
                        label={t`Import Completed:`}
                        value={getFormattedDate(workpackageSchedule.endDatetime)}
                      ></ImportItem>
                      <ImportItem
                        label={t`Time to Import:`}
                        value={getScheduedCompletedTime(
                          workpackageSchedule.startDatetime,
                          workpackageSchedule.endDatetime
                        )}
                      ></ImportItem>
                      <ImportItem
                        label={t`Imported Successfully:`}
                        value={
                          workpackageSchedule.importSummary ? workpackageSchedule.importSummary.totalWorkPackages : 0
                        }
                      ></ImportItem>
                      <ImportItem
                        label={t`Total Errors:`}
                        value={
                          workpackageSchedule.errorSummary ? workpackageSchedule.errorSummary.totalWorkPackages : 0
                        }
                      ></ImportItem>
                      <Separator type={SeparatorType.HORIZONTAL} spacing="600" />
                    </Box>
                  )}
                </Box>
                <FooterStyled>
                  <Box>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled
                        disabled={mutationLoading || !dirty}
                        onClick={() => {
                          handleReset();
                        }}
                        appearance={ButtonAppearance.SECONDARY}
                      >
                        <Trans>Discard Changes</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <FooterButtonStyled
                      onClick={() => handleSubmit()}
                      disabled={!dirty || mutationLoading || !isValid}
                      appearance={ButtonAppearance.SECONDARY}
                    >
                      {mutationLoading && <Spinner />}
                      {!mutationLoading && t`Save Changes`}
                    </FooterButtonStyled>
                  </Box>
                </FooterStyled>
              </Box>
            );
          }}
        </Formik>
      )}
      {loading && (
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {importScheduleError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ImportWpScheduleError error={importScheduleError} close={closeModal}></ImportWpScheduleError>
          </Suspense>
        </ErrorBoundary>
      )}
    </ProjectAdminSubContainerStyled>
  );
};
export default ImportWorkPackages;
